import { type RefObject, useEffect } from "react";
import { inputElementIsActive } from "../utils";
import type { UsePlayerReturn } from "./usePlayer";

export interface UseHotkeysOptions {
  /**
   * Player hook return. The hotkey handler reads `playerRef.current` and
   * the rate-mirror refs synchronously inside the keydown callback (you
   * can't await state updates from a keyboard event).
   */
  player: UsePlayerReturn;
  /**
   * Hotkeys only fire when `document.activeElement` is inside this container
   * (unless `alwaysAllow` is true). Pass the outermost player wrapper so
   * focus on *any* descendant — video surface, controls bar, transcript
   * panel words, etc. — counts as "user is interacting with the player."
   */
  containerRef: RefObject<HTMLElement | null>;
  /** When false, no listener is attached. */
  enabled?: boolean;
  /** Ignore the focus check (e.g. full-screen players that own the page). */
  alwaysAllow?: boolean;
  /** Frames per second — used for arrow-key frame stepping. */
  fps: number;
  /** Called when F is pressed. Component owns this because it depends on the container ref. */
  onFullScreenToggle: () => void;
  /** Called after a JKL rate ramp so the orchestrator can flash the center button. */
  onScrubFeedback?: () => void;
}

/**
 * Keyboard shortcuts following the JKL video-editor convention:
 *
 *   Space / K   play/pause
 *   F           fullscreen
 *   M           mute
 *   ← / →       step 1 frame (Shift = 10 frames)
 *   J           reverse playback / step down rate ladder
 *   L           forward playback / step up rate ladder
 *
 * JKL ramps through preset multipliers: pressing J/L while playing cycles
 * through [-8x, -4x, -2x, -1x, 1x, 2x, 4x, 8x]. Pressing J/L while paused
 * starts playback at +/- 1x and pings the center-button feedback.
 */
export function useHotkeys(options: UseHotkeysOptions): void {
  const {
    player,
    containerRef,
    enabled = true,
    alwaysAllow = false,
    fps,
    onFullScreenToggle,
    onScrubFeedback,
  } = options;

  useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent) => {
      if (inputElementIsActive()) return;
      // Allow hotkeys when focus is inside the player container OR when
      // nothing in particular is focused (activeElement is body / null).
      // Block only when focus is on some unrelated element outside the
      // player (rare in a single-player page, but defensive).
      if (!alwaysAllow && containerRef.current) {
        const ae = document.activeElement;
        const focusOnBody = !ae || ae === document.body;
        const focusInContainer = containerRef.current.contains(ae);
        if (!focusOnBody && !focusInContainer) return;
      }
      const cur = () => player.playerRef.current?.currentTime?.() ?? 0;

      switch (e.key) {
        case " ":
        case "k":
        case "K":
          e.preventDefault();
          player.togglePlay();
          break;
        case "f":
        case "F":
          e.preventDefault();
          onFullScreenToggle();
          break;
        case "m":
        case "M":
          e.preventDefault();
          if (player.playerRef.current) {
            player.setMuted(!player.playerRef.current.muted());
          }
          break;
        case "ArrowLeft":
          e.preventDefault();
          player.seek(cur() + (e.shiftKey ? -10 : -1) / fps);
          break;
        case "ArrowRight":
          e.preventDefault();
          player.seek(cur() + (e.shiftKey ? 10 : 1) / fps);
          break;
        case "j":
        case "J":
          e.preventDefault();
          if (!player.playerRef.current) return;
          if (player.playerRef.current.paused()) {
            player.setRate(
              -Math.abs(player.playbackRateRef.current || 1),
              false,
            );
            player.play();
            onScrubFeedback?.();
          } else {
            stepPlaybackRateMultiple("reverse", player, onScrubFeedback);
          }
          break;
        case "l":
        case "L":
          e.preventDefault();
          if (!player.playerRef.current) return;
          if (player.playerRef.current.paused()) {
            player.setRate(
              Math.abs(player.playbackRateRef.current || 1),
              false,
            );
            player.play();
            onScrubFeedback?.();
          } else {
            stepPlaybackRateMultiple("forward", player, onScrubFeedback);
          }
          break;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [
    enabled,
    alwaysAllow,
    fps,
    player,
    containerRef,
    onFullScreenToggle,
    onScrubFeedback,
  ]);
}

/**
 * Ramp the temp playback rate one step along the bidirectional ladder
 * [-8, -4, -2, -base, base, 2, 4, 8] where `base` is the persisted rate
 * (clamped to [0.25, 2]).
 */
function stepPlaybackRateMultiple(
  direction: "forward" | "reverse",
  player: UsePlayerReturn,
  onScrubFeedback?: () => void,
) {
  if (!player.playerRef.current) return;

  const base = Math.min(
    Math.max(Math.abs(player.playbackRateRef.current || 1), 0.25),
    2,
  );
  const ladderBase = [-8, -4, -2, -base, base, 2, 4, 8];
  const ladder: number[] = [];
  for (const r of ladderBase) {
    if (!ladder.some((x) => Math.abs(x - r) < 0.001)) ladder.push(r);
  }

  const current = player.tempPlaybackRateRef.current ?? base;
  let index = ladder.findIndex((r) => Math.abs(r - current) < 0.001);
  if (index === -1) {
    // Snap to nearest rung if the current rate isn't exactly on the ladder.
    let bestIdx = 0;
    let bestDiff = Number.POSITIVE_INFINITY;
    ladder.forEach((r, i) => {
      const diff = Math.abs(r - current);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    });
    index = bestIdx;
  }

  const step = direction === "forward" ? 1 : -1;
  const nextIndex = Math.max(0, Math.min(ladder.length - 1, index + step));
  player.setRate(ladder[nextIndex], false);
  onScrubFeedback?.();
}
